import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../../services/api';
import Avatar from './Avatar';
import clsx from 'clsx';

interface UserSelectorProps {
  value: string;
  onChange: (userId: string) => void;
  includeAll?: boolean;
  label?: string;
  required?: boolean;
  className?: string;
  filterFn?: (user: { id: string; name: string; role_label?: string | null; photo_url?: string | null }) => boolean;
}

export default function UserSelector({
  value,
  onChange,
  includeAll = false,
  label,
  required,
  className,
  filterFn,
}: UserSelectorProps) {
  const { data } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 60 * 1000,
  });

  const allUsers = data?.data || [];
  const users = filterFn ? allUsers.filter(filterFn) : allUsers;

  return (
    <div className={className}>
      {label && (
        <label className="label">
          {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="flex gap-2 flex-wrap">
        {includeAll && (
          <button
            type="button"
            onClick={() => onChange('all')}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors',
              value === 'all'
                ? 'bg-brand-500 border-brand-500 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            )}
          >
            Todos
          </button>
        )}
        {users.map((user) => (
          <button
            key={user.id}
            type="button"
            onClick={() => onChange(user.id)}
            title={user.name}
            className={clsx(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-sm font-medium transition-colors',
              value === user.id
                ? 'bg-brand-500 border-brand-500 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            )}
          >
            <Avatar name={user.name} photoUrl={user.photo_url} size="xs" />
            <span className="hidden sm:inline">{user.name.split(' ')[0]}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
